import type { JSX, ReactNode } from 'react'

import { Alert, AlertDescription } from '../components/ui/alert.tsx'
import { Icon, type IconName } from './icon.tsx'
import { cn } from '../lib/cn.ts'

const TONES = {
  info: { className: '', icon: 'info' },
  // The glyph takes `warning-ink`, not `warning`. The Blytz amber is a fill
  // and a border in the Consumer Design System, never a mark on white: on a
  // card it is 1.73:1, under the 3:1 floor for a graphic. The border keeps the
  // hue, which is where that system carries it. EI-295.
  warning: {
    className: 'border-warning/50 text-warning-ink [&>svg]:text-warning-ink',
    icon: 'triangle-alert'
  },
  danger: {
    className: 'border-destructive/50 text-destructive',
    icon: 'circle-x'
  }
} as const satisfies Record<string, { className: string; icon: IconName }>

export interface NoticeProps {
  'data-testid': string
  tone?: keyof typeof TONES
  /**
   * Whether assistive technology should interrupt to read this.
   *
   * Default `true` — a notice usually appears in response to something the
   * user just did. Pass `false` when the content changes on a timer: an
   * assertive region re-announces on every change, so a per-second countdown
   * is read out dozens of times and anything focusable inside it is announced
   * as part of the alert.
   */
  announce?: boolean
  children: ReactNode
}

/**
 * Notice — an inline message about the thing on screen.
 *
 * Each tone carries its own glyph, so the meaning is not colour alone.
 *
 * @param props - Notice props.
 * @param props.tone - Which kind of message this is.
 * @param props.children - The message.
 * @returns The rendered notice.
 */
export function Notice({
  'data-testid': testId,
  tone = 'info',
  announce = true,
  children
}: NoticeProps): JSX.Element {
  const { className, icon } = TONES[tone]

  return (
    <Alert
      data-testid={testId}
      role={announce ? 'alert' : 'presentation'}
      aria-live={announce ? undefined : 'off'}
      className={cn(className)}
    >
      <Icon name={icon} />
      <AlertDescription className="text-inherit">{children}</AlertDescription>
    </Alert>
  )
}
